import { ClassNamesConfig, components, GroupBase, SelectComponentsConfig } from 'react-select';
import AsyncCreatable, { AsyncCreatableProps } from 'react-select/async-creatable';
import Creatable, { CreatableProps } from 'react-select/creatable';

import { DebounceProps, useDebouncedLoadOptions } from '@/hooks/use-debounced-load-options';
import { cn } from '@/lib/utils';

import ArrowDownSmallIcon from '../icons/arrow-down-small';
import CloseSmallIcon from '../icons/close-small';

export function CreatableSelect<Option>({ components, ...props }: CreatableProps<Option, false, GroupBase<Option>> & { error?: boolean }) {
  return (
    <Creatable
      {...props}
      classNames={CREATABLE_SELECT_STYLES<Option>(props.error)}
      components={{
        ...CREATABLE_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      unstyled
    />
  );
}

export function AsyncCreatableSelect<Option>({
  components,
  loadOptions,
  onInputChange,
  debounceMs = 0,
  ...props
}: AsyncCreatableProps<Option, false, GroupBase<Option>> & { error?: boolean } & DebounceProps) {
  const debounced = useDebouncedLoadOptions(loadOptions, onInputChange, debounceMs);
  return (
    <AsyncCreatable
      {...props}
      loadOptions={debounced.loadOptions}
      onInputChange={debounced.onInputChange}
      classNames={CREATABLE_SELECT_STYLES<Option>(props.error)}
      components={{
        ...CREATABLE_SELECT_COMPONENTS<Option>(),
        ...components,
      }}
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      unstyled
    />
  );
}

export const CREATABLE_SELECT_COMPONENTS: <Option>() => SelectComponentsConfig<Option, false, GroupBase<Option>> = () => ({
  DropdownIndicator: (props) =>
    props.selectProps.isDisabled ? null : (
      <components.DropdownIndicator {...props}>
        <ArrowDownSmallIcon />
      </components.DropdownIndicator>
    ),
  ClearIndicator: (props) => (
    <components.ClearIndicator {...props}>
      <CloseSmallIcon />
    </components.ClearIndicator>
  ),
});

export const CREATABLE_SELECT_STYLES: <Option>(error?: boolean) => ClassNamesConfig<Option, false> = (error) => ({
  control: (state) =>
    cn(
      'p-[8px] border-grey-3 focus:ring-ring rounded border-2 bg-white px-3 text-sm ring-offset-white focus:outline-none flex items-center',
      state.isFocused && 'border-default-blue',
      error && '!border-default-red',
      state.isDisabled && 'cursor-not-allowed bg-grey-1 text-default-text',
    ),
  menu: () => 'max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-default-text shadow-md mt-1 !pointer-events-auto',
  menuPortal: () => '!z-50 !pointer-events-auto',
  menuList: () => 'p-1',
  option: (state) =>
    cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 !text-sm outline-none',
      state.isFocused && 'bg-grey-1',
      state.isDisabled && 'cursor-default text-secondary-text pointer-events-none',
      state.isSelected && 'bg-grey-1',
    ),
  placeholder: () => 'text-grey-5 leading-none',
  multiValue: () => 'bg-default-light-blue rounded-sm mr-1 font-normal flex items-center',
  multiValueLabel: () => 'pl-[5px] py-0 text-xs text-default-dark-blue',
  multiValueRemove: () => 'hover:bg-transparent px-[2px] rounded-sm focus:ring-2 focus:ring-offset-2 focus:outline-none',
  noOptionsMessage: () => 'text-sm text-grey-5 p-2',
});
